import { HelpCircle, X, ChevronDown, ChevronUp, LifeBuoy, MessageCircle, Brain } from 'lucide-react'; // Added Brain icon
import React, { useState, useEffect, useRef, useCallback } from 'react';


import { openExternalLink } from '../../utils/browserUtils';
import ContextMenuDemo from '../demos/ContextMenuDemo';

import MatchaAISummaryTool from './MatchaAISummaryTool'; // Added import

const HelpMenu = ({ isOpen, onToggle }) => {
  const [activeTutorial, setActiveTutorial] = useState(null);
  const menuRef = useRef(null);

  const toggleDropdown = useCallback(() => {
    onToggle && onToggle(isOpen ? null : 'help');
  }, [isOpen, onToggle]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onToggle && onToggle(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onToggle]);

  const tutorials = [
    {
      id: 'chat-help',
      title: 'Chat Help',
      description: 'Get help from our AI assistant',
      icon: '💬',
      action: () => openExternalLink('https://chatgpt.com/g/g-XkUsLuDTK-aj')
    },
    {
      id: 'right-click-ai',
      title: 'Right-Click for AJ',
      description: 'Discover how AJ, the Amazing Charts AI Assistant, can make your work enjoyable again!',
      icon: '🖱️',
      component: <ContextMenuDemo />
    },
    {
      id: 'matcha-ai-summary',
      title: 'MatchaAI Summary',
      description: 'Use MatchaAI to generate a summary from a payload.',
      icon: <Brain className="w-5 h-5 text-blue-500" />,
      component: <MatchaAISummaryTool />
    },
    // Add more tutorials here
  ];

  const openTutorial = (tutorial) => {
    if (tutorial.action) {
      tutorial.action();
      onToggle && onToggle(null); // Close the menu
      return;
    }
    setActiveTutorial(tutorial);
    onToggle && onToggle(null);
  };

  const closeTutorial = () => {
    setActiveTutorial(null);
  };

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={toggleDropdown}
        className="p-2 hover:bg-opacity-70 flex items-center space-x-1 text-current"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <LifeBuoy className="w-5 h-5" />
        <span className="text-sm">Help</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4" />
        ) : (
          <ChevronDown className="w-4 h-4" />
        )}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200 min-w-[200px]"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="help-menu"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-2 border-b border-gray-100">
            <h3 className="text-sm font-medium text-gray-700">Help & Tutorials</h3>
          </div>
          <div className="py-1 max-h-60 overflow-y-auto">
            {tutorials.map((tutorial) => (
              <button
                key={tutorial.id}
                onClick={() => openTutorial(tutorial)}
                className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center space-x-2"
                role="menuitem"
              >
                <span className="text-lg">{tutorial.icon}</span>
                <span>{tutorial.title}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tutorial Modal */}
      {activeTutorial && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">{activeTutorial.title}</h2>
                <p className="text-sm text-gray-500">{activeTutorial.description}</p>
              </div>
              <button
                onClick={closeTutorial}
                className="text-gray-500 hover:text-gray-700 p-1"
                aria-label="Close tutorial"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto">
              {activeTutorial.component}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpMenu;
